/** WhatsApp helpers: Facebook SDK loader (Embedded Signup) + message builders. */
import { API_ORIGIN } from "@/lib/api";

let _sdkPromise = null;

/** Load the Facebook JS SDK once and init with the operator's app id. */
export function loadFbSdk(appId, version) {
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, cookie: true, xfbml: false, version });
      return resolve(window.FB);
    }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, cookie: true, xfbml: false, version });
      resolve(window.FB);
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => reject(new Error("Could not load the Facebook SDK"));
    document.body.appendChild(s);
  });
  return _sdkPromise;
}

const hi = (name) => (name ? `Hi ${name.split(" ")[0]}, ` : "Hi, ");
const WEB = (typeof window !== "undefined" && window.location?.origin) || API_ORIGIN;

/**
 * Build a WhatsApp click-to-chat link (wa.me) for a phone + pre-filled text.
 * Normalises UK/international numbers; returns null when there's no usable number.
 * Works on WhatsApp mobile and WhatsApp Web.
 */
export function waLink(phone, text) {
  if (!phone) return null;
  let d = String(phone).trim().replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = `44${d.slice(1)}`; // UK national → international
  d = d.replace(/\D/g, "");
  if (d.length < 8 || d.length > 15) return null;
  return `https://wa.me/${d}?text=${encodeURIComponent(text || "")}`;
}

export function waCustomerText(c, garageName) {
  return `${hi(c?.name)}this is ${garageName || "your garage"}. `;
}

export function waBookingText(b, { customerName, garageName, when, token } = {}) {
  const link = token ? ` You can confirm or reschedule here: ${WEB}/b/${token}` : "";
  return `${hi(customerName)}your booking with ${garageName || "us"}${when ? ` is confirmed for ${when}` : " is confirmed"}.${link}`;
}

export function waBookingReminderText(b, { customerName, garageName, when, token } = {}) {
  const link = token ? ` Manage your booking: ${WEB}/b/${token}` : "";
  return `${hi(customerName)}a reminder of your appointment with ${garageName || "us"}${when ? ` on ${when}` : ""}.${link}`;
}

export function waVehicleReadyText({ customerName, garageName, vrm } = {}) {
  return `${hi(customerName)}good news — your vehicle${vrm ? ` ${vrm}` : ""} is ready for collection at ${garageName || "the garage"}. Please get in touch to arrange pick-up.`;
}

export function waQuoteText(q, { customerName, garageName } = {}) {
  const amt = q?.totals?.gross != null ? ` for £${Number(q.totals.gross).toFixed(2)}` : "";
  return `${hi(customerName)}your quotation ${q?.quotation_number || ""}${amt} from ${garageName || "us"} is ready. Please reply to approve or if you have any questions.`;
}

export function waApprovalText({ customerName, garageName, link, title } = {}) {
  return `${hi(customerName)}${garageName || "we"} need your approval for additional work${title ? ` (${title})` : ""}. Please review and approve here: ${link || ""}`;
}

export function waJobUpdateText({ customerName, garageName, title, status } = {}) {
  return `${hi(customerName)}an update on your vehicle at ${garageName || "the garage"}${title ? ` — ${title}` : ""}${status ? `: ${status.replace(/_/g, " ")}` : ""}.`;
}

export function waInvoiceText(inv, { customerName, garageName } = {}) {
  const amt = inv?.total_gross != null ? ` for £${Number(inv.total_gross).toFixed(2)}` : "";
  return `${hi(customerName)}your invoice ${inv?.invoice_number || ""}${amt} from ${garageName || "us"} is ready. Thank you for your business.`;
}

export function waPaymentReminderText(inv, { customerName, garageName } = {}) {
  const bal = inv?.balance != null ? ` of £${Number(inv.balance).toFixed(2)}` : "";
  return `${hi(customerName)}a friendly reminder that invoice ${inv?.invoice_number || ""} from ${garageName || "us"} has an outstanding balance${bal}. Please let us know if you have any questions.`;
}
